import { Inject, Injectable, Optional } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SessionServiceConstants } from './constants-v2';
import { Broadcaster } from './events-v7';
import { IApplicationMenu, ILegalEntity } from '@cemex-core/types-v2/dist/index.interface';
import { HttpCemex } from './http.service';
import {
  dataDecrypt,
  dataEncrypt,
  parseBase64StorageJSON,
  parseFromStorage,
  safeParse,
  storeBase64StorageJSON,
} from './functions';
import { CmxAuth } from './models/cemex-auth';

export enum Regions {
  AMEA = 'AMEA',
  EU = 'Europe',
  MX = 'Mexico',
  US = 'US',
  SCACC = 'SCA&CC',
}

@Injectable()
export class SessionService {
  public static LOGIN_SUCCESS_EVENT = 'LOGIN_SUCCESS_EVENT';
  public static LOGIN_FAIL_EVENT = 'LOGIN_FAIL_EVENT';
  public static LOGIN_LOGOUT_EVENT = 'LOGIN_LOGOUT_EVENT';
  public static AUTH_TOKEN_VERSION = 'auth_token_version';

  private readonly _siteDomain = window['SITE_DOMAIN'] || '';
  private readonly _endpoint: string;
  private readonly _menuApplication = new BehaviorSubject<IApplicationMenu[]>([]);
  private readonly _currentLegalEntity = new BehaviorSubject<ILegalEntity | null>(null);
  private _userProfile: CmxAuth['profile'] | null = null;
  private _beforeLogout: Observable<any> | null = null;
  private _dataSession: CmxAuth | null = null;

  private readonly europeCountries = ['CZ', 'DE', 'ES', 'FR', 'GB', 'PL'];
  private readonly ameaCountries = ['AE', 'EG', 'IL', 'PH'];
  private readonly scaccCountries = ['CO', 'CR', 'DO', 'GT', 'NI', 'PA', 'PE', 'PR', 'SV'];

  public cxAppName = window['CX_APP_NAME'] || '';

  constructor(
    protected http: HttpCemex,
    protected eventBroadcaster: Broadcaster,
    @Inject(SessionService.AUTH_TOKEN_VERSION)
    @Optional()
    private authTokenVersion: string,
  ) {
    if (authTokenVersion) {
      switch (authTokenVersion.toLocaleLowerCase()) {
        case 'v6':
          this._endpoint = SessionServiceConstants.LOGIN_TOKEN_V6;
          break;
        default:
          this._endpoint = SessionServiceConstants.LOGIN_TOKEN_DEFAULT;
      }
    } else {
      this._endpoint = SessionServiceConstants.LOGIN_TOKEN_DEFAULT;
    }
    if (
      localStorage.getItem('language') === undefined ||
      (localStorage.getItem('language') === null &&
        sessionStorage.getItem('language') !== undefined &&
        sessionStorage.getItem('language') !== null)
    ) {
      const language = sessionStorage.getItem('language');
      if (language) {
        localStorage.setItem('language', language);
      }
    }

    if (this.cxAppName !== '') {
      if (this.dataSession) {
        this.processDataFromLogin(this.dataSession);
      }
    } else {
      this.cleanSessionStorge();
    }
  }

  public menuApplicationItemsParam(applicationCode?: string): Observable<IApplicationMenu[]> {
    if (this._menuApplication.getValue().length === 0) {
      this.reloadAppMenuItems(applicationCode);
    }
    return this._menuApplication.asObservable();
  }

  public setLegalEntity(value: ILegalEntity) {
    sessionStorage.setItem('user_legal_entity', JSON.stringify(value));
    this.eventBroadcaster.broadcast(Broadcaster.DCM_LEGAL_ENTITY_CHANGE, JSON.stringify(value));
    this._currentLegalEntity.next(value);
  }

  public logout(): Observable<{
    success: boolean;
    message: string;
  }> {
    return new Observable((observer) => {
      if (this._beforeLogout) {
        this._beforeLogout.subscribe(
          (result) => {
            this.logoutApp();
            observer.next({
              message: 'success',
              success: true,
            });
          },
          (error) => {
            observer.next({
              message: error,
              success: false,
            });
          },
        );
      } else {
        this.logoutApp();
        observer.next({
          message: 'success',
          success: true,
        });
      }

      return observer;
    });
  }

  public login(
    user: string,
    password: string,
    generateCookie?: boolean,
    appName?: string,
    validateTempPassword?: boolean,
    grant_type?: string,
    scope?: string,
    userInformation?: string,
    include?: string,
    mprCode?: boolean,
    consent?: any,
  ): Promise<any> {
    this.clean();
    let options = new HttpHeaders();
    options = options.set('Content-Type', 'application/json');
    options = options.set('accept', 'text/plain, */*');
    mprCode ? options.set('App-Code', 'MPR') : null;
    const endpoint = this.http.generateEndpoint(this._endpoint);
    const urlPost = this.generateJsonBody(
      user,
      password,
      appName,
      grant_type,
      scope,
      userInformation,
      include,
      consent,
    );
    return this.http
      .post(endpoint, urlPost, options)
      .toPromise()
      .then(async (response: any) => {
        const data: CmxAuth = response;
        this.saveProcessDataFromLogin(data, generateCookie);
        this.eventBroadcaster.broadcast(SessionService.LOGIN_SUCCESS_EVENT);
        if (validateTempPassword && data.profile && data.profile.hasTemporaryPassword) {
          return Promise.resolve(data);
        }
        return Promise.resolve(undefined);
      })
      .catch((error: any) => {
        console.error(error);
        this.eventBroadcaster.broadcast(SessionService.LOGIN_FAIL_EVENT);
        return Promise.reject(error);
      });
  }

  public getLegalEntities(fetch?: number, page?: number, legalEntityCode?: string): Observable<ILegalEntity[]> {
    let endpoint = this.http.generateEndpoint(SessionServiceConstants.USER_LEGAL_ENTITIES);
    endpoint = this.addQueryParams(endpoint, fetch, page, legalEntityCode);

    return this.http.get(endpoint).pipe(
      map((response: any) => {
        return response ? response : [];
      }),
      catchError((e) => throwError(() => this.handleError(e))),
    );
  }

  public getLegalEntities_v2(body?: any): Observable<ILegalEntity[]> {
    let endpoint = this.http.generateEndpoint(SessionServiceConstants.USER_LEGAL_ENTITIES);
    return this.http.post(endpoint, body).pipe(
      map((response: any) => {
        return response ? response : [];
      }),
      catchError((e) => throwError(() => this.handleError(e))),
    );
  }

  public setBeforeLogout(method: Observable<any>): void {
    this._beforeLogout = method;
  }

  public getBeforeLogout(): Observable<any> | null {
    return this._beforeLogout;
  }

  public reloadAppMenuItems(applicationCode?: string): void {
    this.getDCMApplications(applicationCode).subscribe((result) => {
      this._menuApplication.next(result);
    });
  }

  public createCookie(name: string, value: string, days?: number) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = date.toUTCString();
    } else {
      days = 0;
    }

    const cookie = `${name}=${value};expires=${expires};domain=${this._siteDomain};path=/`;
    document.cookie = cookie;
  }

  public readCookie(name: string): string {
    const ca = document.cookie.split(';');
    const caLen: number = ca.length;
    const cookieName = `${name}=`;
    let c: string;
    for (let i = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.indexOf(cookieName) === 0) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }

  public clearCookie(name: any) {
    this.createCookie(name, '', -1);
  }

  public setUserProfile(): void {
    const tmpProfile = this.profile;
    if (tmpProfile) {
      const userCustomer = this.customer;
      if (userCustomer) {
        tmpProfile.customer = userCustomer;
      }
      this._userProfile = tmpProfile;
    }
  }

  public clean(): void {
    this.clearCookie('CemexGoRegion');
    this.clearCookie('up_session');
    this.cleanSessionStorge();
    sessionStorage.removeItem('_cmx_');
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('role');
    sessionStorage.removeItem('country');
    sessionStorage.removeItem('user_legal_entity');
    sessionStorage.removeItem('region');
    sessionStorage.removeItem('user_customer');
    sessionStorage.removeItem('username');
  }

  public generateUrlString(user: string, password: string, appName?: string): string {
    const include = '&include=userinfo,profile,applications,roles,customers,oldVersion';
    let url =
      'grant_type=password&scope=security&username=' +
      user +
      '&password=' +
      encodeURIComponent(password) +
      '&client_id=' +
      this.http.clientId;

    if (appName) {
      url = url + '&app_name=' + appName;
    }
    if (this.authTokenVersion) {
      if (this.authTokenVersion.toLowerCase() === 'v6') {
        url = `${url}&userinformation=false${include}`;
      }
    }
    return url;
  }

  public generateJsonBody(
    userName: string,
    password: string,
    appName?: string,
    grant_type?: string,
    scope?: string,
    userInformation?: string,
    include?: string,
    consent?: any,
  ): string {
    const body = {
      grant_type: grant_type != null ? grant_type : 'password',
      scope: scope != null ? scope : 'security',
      username: userName,
      password: password,
      app_name: appName,
      userInformation: userInformation != null ? userInformation : 'false',
      include: include != null ? include : 'userinfo,profile,applications,roles,customers,oldVersion',
      consent: consent,
    };

    return JSON.stringify(body);
  }

  public getDCMApplications(applicationCode?: string): Observable<IApplicationMenu[]> {
    let endpoint = this.http.generateEndpoint(SessionServiceConstants.USER_APPLICATION_MENU);
    if (applicationCode) {
      endpoint = endpoint + '?applicationCode=' + applicationCode;
    }
    return this.http.get(endpoint).pipe(
      map((response: any) => {
        const tmpJson = response;
        if (!tmpJson || tmpJson === undefined) {
          console.warn('Configuration of menu application is empty!!');
          return [];
        }
        const responseMap: IApplicationMenu[] = response as IApplicationMenu[];
        return responseMap;
      }),
      catchError((e) => throwError(() => this.handleError(e))),
    );
  }

  public convertJsonTo<T>(obj: string): T {
    const errMessage = 'Exception at parsing object from sessionStore:';
    return safeParse<T>(obj, errMessage);
  }

  public handleError(error: Response | any): any {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof Response) {
      errMsg = `${error.status} - ${error.statusText || ''}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return throwError(() => this.handleError(error));
  }

  public addQueryParams(endpoint: string, fetch?: number, page?: number, legalEntityCode?: string) {
    const validPagination: boolean = Number.isInteger(fetch) && Number.isInteger(page);

    endpoint += validPagination ? '?fetch=' + fetch + '&page=' + page : '';
    endpoint +=
      legalEntityCode && validPagination
        ? '&legalEntityCode=' + legalEntityCode
        : legalEntityCode && !validPagination
          ? '?legalEntityCode=' + legalEntityCode
          : '';

    return endpoint;
  }

  public logoutApp(): void {
    this.clean();
    this.eventBroadcaster.broadcast(Broadcaster.DCM_APP_LOGOUT);
  }

  public getUserRegionByCountry(countryCode?: string): string {
    if (!countryCode) {
      countryCode = this.userCountry;
    }

    let region = '';
    if (countryCode === 'MX') {
      region = Regions.MX;
    } else if (countryCode === 'US') {
      region = Regions.US;
    } else if (!!this.scaccCountries.find((c) => c === countryCode)) {
      region = Regions.SCACC;
    } else if (!!this.europeCountries.find((c) => c === countryCode)) {
      region = Regions.EU;
    } else if (!!this.ameaCountries.find((c) => c === countryCode)) {
      region = Regions.AMEA;
    }
    return region;
  }

  public saveProcessDataFromLogin(data: CmxAuth, generateCookie?: boolean) {
    this._dataSession = data;
    storeBase64StorageJSON('session', '_cmx_', data);

    if (generateCookie) {
      const sessionCookie = {
        auth: data.oauth2.access_token,
        expires_in: data.oauth2.expires_in,
        jwt: data.jwt,
        refresh_token: data.oauth2.refresh_token,
        region: data.oauth2.region,
      };
      this.createCookie('up_session', JSON.stringify(sessionCookie));
    }
    this.createCookie('CemexGoRegion', data.oauth2.region);
    this.setUserProfile();
    sessionStorage.setItem('sessionId', data.profile.userId.toString());
    sessionStorage.setItem('role', data.role);
    sessionStorage.setItem('country', data.country);
    sessionStorage.setItem('username', data.profile.userAccount);
  }

  // getters
  public get dataSession(): CmxAuth | null {
    if (!this._dataSession) {
      this._dataSession = parseBase64StorageJSON<CmxAuth>('session', '_cmx_');
    }
    return this._dataSession;
  }

  public get applicationsList(): CmxAuth['applications'] {
    return (this.dataSession && this.dataSession.applications) || [];
  }

  public get access_token(): string {
    return (this.dataSession && this.dataSession.oauth2 && this.dataSession.oauth2.access_token) || '';
  }

  public get refresh_token(): string {
    return (this.dataSession && this.dataSession.oauth2 && this.dataSession.oauth2.refresh_token) || '';
  }

  public get jwt(): string {
    return (this.dataSession && this.dataSession.jwt) || '';
  }

  public get profile(): CmxAuth['profile'] | null {
    return (this.dataSession && this.dataSession.profile) || null;
  }

  public get sessionId(): number | null {
    return (this.profile && this.profile.userId) || null;
  }

  public get customerDetail(): CmxAuth['customer'] | null {
    return (this.dataSession && this.dataSession.customer) || null;
  }

  public get expiresIn() {
    return (this.dataSession && this.dataSession.oauth2 && this.dataSession.oauth2.expires_in) || null;
  }

  public get role(): string {
    return (this.dataSession && this.dataSession.role) || '';
  }

  public get username(): string {
    return (this.profile && this.profile.userAccount) || '';
  }

  public get menuApplicationItems(): Observable<IApplicationMenu[]> {
    if (this._menuApplication.getValue().length === 0) {
      this.reloadAppMenuItems();
    }
    return this._menuApplication.asObservable();
  }

  public get currentLegalEntity(): Observable<ILegalEntity> {
    return this._currentLegalEntity.asObservable();
  }

  public get currentLegalEntityValue(): ILegalEntity | null {
    return this._currentLegalEntity.getValue() || parseFromStorage<ILegalEntity>('session', 'user_legal_entity');
  }

  public get isLoggedIn(): boolean {
    return Boolean(this.access_token && this.jwt);
  }

  public get userRegion(): string {
    return (this.dataSession && this.dataSession.oauth2.region) || 'US';
  }

  public get userCountry(): string {
    return (this.dataSession && this.dataSession.country) || '';
  }

  public get country(): string {
    return this.userCountry;
  }

  public get userProfile(): CmxAuth['profile'] | null {
    if (!this._userProfile) {
      this.setUserProfile();
    }
    return this._userProfile;
  }

  public get customer(): CmxAuth['customer'] | null {
    return (this.dataSession && this.dataSession.customer) || null;
  }

  public get userSegment(): string {
    return sessionStorage.getItem('userSegment') || '';
  }

  public get userSubsegment(): string {
    return sessionStorage.getItem('userSubsegment') || '';
  }

  public encrypt(str: any): string {
    return dataEncrypt(str);
  }

  public decrypt(str: string): string {
    return dataDecrypt(str);
  }

  public updateDataSession(data: CmxAuth): void {
    if (this.dataSession === null) {
      this._dataSession = data;
      storeBase64StorageJSON('session', '_cmx_', data);
    } else {
      let dataSession = this.dataSession;
      Object.entries(data).forEach(([key]) => {
        if (key === 'oauth2' && dataSession['oauth2']) {
          dataSession.oauth2.access_token = data.oauth2.access_token || dataSession.oauth2.access_token;
          dataSession.oauth2.refresh_token = data.oauth2.refresh_token || dataSession.oauth2.refresh_token;
          dataSession.oauth2.expires_in = data.oauth2.expires_in || dataSession.oauth2.expires_in;
          dataSession.oauth2.scope = data.oauth2.scope || dataSession.oauth2.scope;
          dataSession.oauth2.region = data.oauth2.region || dataSession.oauth2.region;
        } else if (data[key]) {
          dataSession[key] = data[key];
        }
      });
      this._dataSession = dataSession;
      storeBase64StorageJSON('session', '_cmx_', dataSession);
    }
  }

  public processDataFromLogin(data: CmxAuth, generateCookie?: boolean): void {
    // session cookie
    if (generateCookie) {
      const sessionCookie = {
        auth: data.oauth2.access_token,
        expires_in: data.oauth2.expires_in,
        jwt: data.jwt,
        refresh_token: data.oauth2.refresh_token,
        region: data.oauth2.region,
      };
      this.createCookie('up_session', JSON.stringify(sessionCookie));
      this.setUserProfile();
    }

    if (data.oauth2) {
      this.createCookie('CemexGoRegion', data.oauth2.region);

      sessionStorage.setItem('access_token', data.oauth2.access_token);
      sessionStorage.setItem('refresh_token', data.oauth2.refresh_token);
      sessionStorage.setItem('expires_in', data.oauth2.expires_in.toString());
      sessionStorage.setItem('jwt', data.jwt);
      sessionStorage.setItem('auth_token', data.oauth2.access_token);
      sessionStorage.setItem('user_profile', JSON.stringify(data.profile));
      sessionStorage.setItem('user_customer', JSON.stringify(data.customer));
      sessionStorage.setItem('user_applications', JSON.stringify(data.applications));
      sessionStorage.setItem('sessionId', data.profile.userId.toString());

      // react
      sessionStorage.setItem('userInfo', JSON.stringify(data));
      sessionStorage.setItem('country', data.country);
      sessionStorage.setItem('token_data', JSON.stringify(data));
      sessionStorage.setItem('applications', JSON.stringify(data.applications));

      sessionStorage.setItem('region', data.oauth2.region);
      sessionStorage.setItem('role', data.role);
      sessionStorage.setItem('username', data.profile.userAccount);

      this.setUserProfile();
    }
  }

  public cleanSessionStorge() {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('expires_in');
    sessionStorage.removeItem('jwt');
    sessionStorage.removeItem('user_profile');
    sessionStorage.removeItem('user_applications');
    // patch-fix for react
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('token_data');
    sessionStorage.removeItem('applications');
  }
}
